import { Routes, Route, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Layers, Database, SquarePlus, Activity as ActivityIcon, Bell,
  ServerCog, Braces, DownloadCloud, ChevronsUpDown, Check, Plus,
  Sun, Moon, LogOut, ChevronDown, FolderOpen, Users, Mail, Menu, GitBranch, CreditCard, Wallet, Gauge, Landmark, Settings as SettingsIcon, Monitor, PanelsTopLeft, Rocket,
  LifeBuoy, X, Pencil, ListChecks,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "./lib/api.js";
import Dashboard from "./pages/Dashboard.jsx";
import ServiceDetail from "./pages/ServiceDetail.jsx";
import Databases from "./pages/Databases.jsx";
import DatabaseDetail from "./pages/DatabaseDetail.jsx";
import NewService from "./pages/NewService.jsx";
import NewAppService from "./pages/NewAppService.jsx";
import NewDatabase from "./pages/NewDatabase.jsx";
import Activity from "./pages/Activity.jsx";
import Deployments from "./pages/Deployments.jsx";
import NotificationSettings from "./pages/NotificationSettings.jsx";
import SharedVars from "./pages/SharedVars.jsx";
import Servers from "./pages/Servers.jsx";
import Fleet from "./pages/Fleet.jsx";
import Consoles from "./pages/Consoles.jsx";
import ImportRender from "./pages/ImportRender.jsx";
import Email from "./pages/Email.jsx";
import Projects from "./pages/Projects.jsx";
import ProjectDetail from "./pages/ProjectDetail.jsx";
import DocsCustomDomains from "./pages/DocsCustomDomains.jsx";
import Customers from "./pages/Customers.jsx";
import Team from "./pages/Team.jsx";
import Clients from "./pages/Clients.jsx";
import ClientServices from "./pages/ClientServices.jsx";
import NewServiceGit from "./pages/NewServiceGit.jsx";
import Billing from "./pages/Billing.jsx";
import StripeAdmin from "./pages/StripeAdmin.jsx";
import EmailTemplates from "./pages/EmailTemplates.jsx";
import WalletPage from "./pages/Wallet.jsx";
import Usage from "./pages/Usage.jsx";
import Settings from "./pages/Settings.jsx";
import ObjectStorage from "./pages/ObjectStorage.jsx";
import ComposeServices from "./pages/ComposeServices.jsx";
import GitHubSetupHelp from "./pages/GitHubSetupHelp.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import Login from "./pages/Login.jsx";
import { AuthProvider, RequireAuth, useAuth } from "./auth.jsx";
import { ThemeProvider, useTheme } from "./lib/theme.jsx";
import { ProjectProvider, useProjects } from "./lib/projects.jsx";
import { OnboardingProvider } from "./lib/onboarding.jsx";
import { OnboardingBanner, GateHost } from "./components/Onboarding.jsx";
import { registerSW } from "virtual:pwa-register";

// ─── Project Switcher ────────────────────────────────────────────────────────

function ProjectSwitcher() {
  const { projects, activeProject, setActive } = useProjects();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const tile = (color) => ({
    width: 22, height: 22, borderRadius: 6,
    background: color, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 700, flexShrink: 0,
  });

  return (
    <div ref={ref} style={{ position: "relative", padding: "0 12px 12px" }}>
      {/* trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 8, padding: "8px 10px", borderRadius: 6,
          border: "1px solid var(--border)", background: "var(--surface)",
          cursor: "pointer", width: "100%", transition: "background .15s, border-color .15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.background = "var(--surface-2)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--surface)"; }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
          <span style={tile(activeProject.color)}>
            {activeProject.name[0].toUpperCase()}
          </span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0 }}>
            <span style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Project
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {activeProject.name}
            </span>
          </div>
        </div>
        <ChevronsUpDown size={15} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
      </button>

      {/* dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 12, right: 12, marginTop: 5,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 8, boxShadow: "var(--shadow-lg)", padding: 6, zIndex: 40,
        }}>
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => { setActive(p.id); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "7px 8px", borderRadius: 6, cursor: "pointer",
                width: "100%", background: "transparent", border: "none",
                transition: "background .15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--surface-2)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <span style={tile(p.color)}>{p.name[0].toUpperCase()}</span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: "var(--text)", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.name}
              </span>
              {p.id === activeProject.id && (
                <Check size={15} style={{ color: "var(--accent)", flexShrink: 0 }} />
              )}
            </button>
          ))}

          <div style={{ height: 1, background: "var(--border)", margin: "6px 4px" }} />

          <button
            onClick={() => { setOpen(false); navigate("/projects"); }}
            style={{
              display: "flex", alignItems: "center", gap: 9,
              padding: "7px 8px", borderRadius: 6, cursor: "pointer",
              width: "100%", background: "transparent", border: "none",
              color: "var(--accent-text)", fontSize: 13, fontWeight: 600,
              transition: "background .15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--accent-soft)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <Plus size={16} />
            New project
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function Sidebar({ drawerOpen, onClose }) {
  const { user } = useAuth();

  const navLink = ({ isActive }) => ({
    display: "flex", alignItems: "center", gap: 11,
    padding: "8px 11px", borderRadius: 6,
    textDecoration: "none", fontSize: 13.5, fontWeight: isActive ? 600 : 500,
    color: isActive ? "var(--accent-text)" : "var(--text-muted)",
    background: isActive ? "var(--accent-soft)" : "transparent",
    transition: "background .15s, color .15s",
  });

  // hover handled via onMouse events on the <NavLink> wrapper
  function HoverNavLink({ to, end, children }) {
    const [hov, setHov] = useState(false);
    return (
      <NavLink
        to={to}
        end={end}
        onClick={onClose}
        style={({ isActive }) => ({
          ...navLink({ isActive }),
          ...(hov && !isActive ? { background: "var(--surface-2)", color: "var(--text)" } : {}),
        })}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
      >
        {children}
      </NavLink>
    );
  }

  return (
    <aside
      className={`sidebar-drawer${drawerOpen ? " is-open" : ""}`}
      style={{
        width: 240, flexShrink: 0, height: "100%",
        display: "flex", flexDirection: "column",
        background: "var(--surface)", borderRight: "1px solid var(--border)",
      }}
    >
      {/* Brand */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "16px 18px 12px" }}>
        {/* 46px so the pulse rings are actually legible; the negative margin
            absorbs the mark's ~20% viewBox padding that the rings expand into,
            keeping the hexagon tight against the wordmark. */}
        <img src="/icon.svg" alt="" width={46} height={46} style={{ display: "block", margin: -5 }} />
        <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 19, letterSpacing: "-0.015em", color: "var(--text)" }}>
          Debut<span style={{ color: "var(--accent-text)" }}>Deploy</span>
        </span>
      </div>

      {/* Project switcher */}
      <ProjectSwitcher />

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "6px 12px", display: "flex", flexDirection: "column", gap: 2 }}>
        <HoverNavLink to="/projects"><FolderOpen size={18} /><span>Projects</span></HoverNavLink>
        <HoverNavLink to="/" end><Layers size={18} /><span>Services</span></HoverNavLink>
        {user?.role === "admin" && <HoverNavLink to="/servers"><ServerCog size={18} /><span>Servers</span></HoverNavLink>}
        <HoverNavLink to="/databases"><Database size={18} /><span>Databases</span></HoverNavLink>
        <HoverNavLink to="/object-storage"><Database size={18} /><span>S3 Storage</span></HoverNavLink>
        {user?.role === "admin" && <HoverNavLink to="/compose"><Braces size={18} /><span>Compose stacks</span></HoverNavLink>}
        <HoverNavLink to="/deployments"><Rocket size={18} /><span>Deployments</span></HoverNavLink>
        <HoverNavLink to="/new"><SquarePlus size={18} /><span>New Service</span></HoverNavLink>
        {(user?.role === "admin" || user?.orgRole === "owner" || user?.orgRole === "manager") && (
          <HoverNavLink to="/import"><DownloadCloud size={18} /><span>Import from Render</span></HoverNavLink>
        )}
        <HoverNavLink to="/activity"><ActivityIcon size={18} /><span>Activity</span></HoverNavLink>
        <HoverNavLink to="/notifications"><Bell size={18} /><span>Notifications</span></HoverNavLink>

        {(user?.orgRole === "owner" || user?.orgRole === "manager") && (
          <HoverNavLink to="/team"><Users size={18} /><span>Team</span></HoverNavLink>
        )}

        {user?.orgRole && (
          <HoverNavLink to="/wallet"><Wallet size={18} /><span>Wallet</span></HoverNavLink>
        )}

        {user?.orgRole && (
          <HoverNavLink to="/usage"><Gauge size={18} /><span>Usage</span></HoverNavLink>
        )}

        {(user?.orgRole || user?.role === "admin") && (
          <HoverNavLink to="/email"><Mail size={18} /><span>Email</span></HoverNavLink>
        )}

        {user?.role === "admin" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 10px 6px" }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.09em", color: "var(--text-muted)", textTransform: "uppercase" }}>Master Admin</span>
              <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
            </div>
            <HoverNavLink to="/clients"><Users size={18} /><span>Clients</span></HoverNavLink>
            <HoverNavLink to="/client-services"><ListChecks size={18} /><span>Client services</span></HoverNavLink>
            <HoverNavLink to="/billing"><CreditCard size={18} /><span>Billing &amp; Plans</span></HoverNavLink>
            <HoverNavLink to="/stripe"><Landmark size={18} /><span>Stripe</span></HoverNavLink>
            <HoverNavLink to="/email-templates"><Mail size={18} /><span>Email templates</span></HoverNavLink>
            <HoverNavLink to="/new-git"><GitBranch size={18} /><span>Deploy from Git</span></HoverNavLink>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 10px 6px" }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.09em", color: "var(--text-muted)", textTransform: "uppercase" }}>Infrastructure</span>
              <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
            </div>
            <HoverNavLink to="/fleet"><Monitor size={18} /><span>Fleet</span></HoverNavLink>
            <HoverNavLink to="/consoles"><PanelsTopLeft size={18} /><span>Consoles</span></HoverNavLink>
            <HoverNavLink to="/shared-vars"><Braces size={18} /><span>Variable Groups</span></HoverNavLink>
          </>
        )}
      </nav>

      {/* Footer */}
      <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{ fontSize: 11.5, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>v1.4.2</span>
        <span style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--text-muted)", opacity: 0.6 }} />
        <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>Coolify 4.0</span>
        <a
          href="/api/status"
          target="_blank"
          rel="noreferrer"
          style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--text-muted)", textDecoration: "none" }}
        >
          Status ↗
        </a>
      </div>
    </aside>
  );
}

// ─── Topbar ──────────────────────────────────────────────────────────────────

const CRUMB_MAP = {
  "/": "Services",
  "/deployments": "Deployments",
  "/projects": "Projects",
  "/databases": "Infrastructure",
  "/new": "New Service",
  "/new/app": "Application service",
  "/new/database": "New database",
  "/new/server": "New server",
  "/new/storage": "S3 storage",
  "/new-database": "New Database",
  "/activity": "Activity",
  "/notifications": "Notifications",
  "/servers": "Servers",
  "/fleet": "Fleet",
  "/consoles": "Consoles",
  "/shared-vars": "Variable Groups",
  "/import": "Import from Render",
  "/email": "Email",
  "/customers": "Customers",
  "/team": "Team",
  "/clients": "Clients",
  "/client-services": "Client services",
  "/new-git": "Deploy from Git",
  "/billing": "Billing & Plans",
  "/stripe": "Stripe",
  "/email-templates": "Email templates",
  "/wallet": "Wallet",
  "/usage": "Usage",
  "/settings": "Account settings",
};

function Topbar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [logoutHov, setLogoutHov] = useState(false);
  const [themeHov, setThemeHov] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    api.health().then((h) => setMode(h.mode)).catch(() => setMode("offline"));
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const crumb = CRUMB_MAP[location.pathname] ??
    (location.pathname.startsWith("/services/") ? "Service Detail" :
     location.pathname.startsWith("/databases/") ? "Database" :
     location.pathname.startsWith("/projects/") ? "Project Detail" : "");

  const isLive = mode === "live";
  const envLabel = mode === "demo" ? "Demo" : mode === "live" ? "Live" : null;

  const initials = user?.name
    ? user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : (user?.email?.[0] ?? "?").toUpperCase();

  return (
    <header className="topbar" style={{
      height: 56, flexShrink: 0,
      borderBottom: "1px solid var(--border)", background: "var(--surface)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 18px 0 24px",
    }}>
      {/* Left: hamburger (mobile/tablet) + breadcrumb.
          minWidth:0 lets this cluster shrink so a long crumb ellipsises instead of
          pushing the right cluster off-screen (320px leaves it only ~65px). */}
      <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0, flex: 1 }}>
        <button
          className="topbar-menu-btn"
          onClick={onMenuClick}
          aria-label="Open menu"
          style={{
            display: "none", /* overridden to flex by CSS on ≤1024px */
            alignItems: "center", justifyContent: "center",
            width: 40, height: 40, borderRadius: 6,
            border: "none", background: "transparent",
            cursor: "pointer", color: "var(--text-muted)",
            marginLeft: -6, flexShrink: 0,
          }}
        >
          <Menu size={20} />
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 9, color: "var(--text-muted)", fontSize: 13, minWidth: 0 }}>
          <span
            title={crumb}
            style={{
              fontWeight: 500, color: "var(--text)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {crumb}
          </span>
        </div>
      </div>

      {/* Right cluster — never shrinks; the crumb yields instead */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {/* Env pill */}
        {envLabel && (
          <div className="topbar-env-pill" style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "5px 11px", borderRadius: 999,
            fontSize: 12, fontWeight: 600,
            background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)",
          }}>
            <span style={{
              width: 7, height: 7, borderRadius: "50%",
              background: isLive ? "var(--ok)" : "var(--warn)",
              boxShadow: isLive ? "0 0 0 3px var(--ok-soft)" : "0 0 0 3px var(--warn-soft)",
            }} />
            {envLabel}
            <span style={{ opacity: 0.6, fontWeight: 450 }}> · Coolify</span>
          </div>
        )}

        {/* Divider */}
        <span className="topbar-divider" style={{ width: 1, height: 22, background: "var(--border)", margin: "0 2px" }} />

        {/* Theme toggle */}
        <button
          className="topbar-theme-btn"
          onClick={toggle}
          title="Toggle theme"
          onMouseEnter={() => setThemeHov(true)}
          onMouseLeave={() => setThemeHov(false)}
          style={{
            width: 34, height: 34, borderRadius: 6,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", border: "1px solid transparent",
            background: themeHov ? "var(--surface-2)" : "transparent",
            color: themeHov ? "var(--text)" : "var(--text-muted)",
            transition: "background .15s, color .15s",
          }}
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* User chip + account menu */}
        <div ref={menuRef} style={{ position: "relative" }}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              display: "flex", alignItems: "center", gap: 9,
              padding: "4px 8px 4px 5px", borderRadius: 6, cursor: "pointer",
              // matches the 40px floor index.css gives the other topbar buttons;
              // without it this collapses to 36px once the name is hidden <640px
              minHeight: 40, flexShrink: 0,
              border: "1px solid transparent", background: menuOpen ? "var(--surface-2)" : "transparent",
              transition: "background .15s",
            }}
          >
            <span style={{
              width: 28, height: 28, borderRadius: "50%",
              background: "linear-gradient(135deg,#6366f1,#2563eb)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 11.5, fontWeight: 600, letterSpacing: "0.02em",
            }}>
              {initials}
            </span>
            <div className="topbar-user-name" style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, textAlign: "left", minWidth: 0 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user?.name || user?.email || "User"}
              </span>
              <span style={{ fontSize: 10.5, color: "var(--text-muted)" }}>
                {user?.role || "member"}
              </span>
            </div>
            <ChevronDown size={14} style={{ color: "var(--text-muted)", marginLeft: 1 }} />
          </button>

          {menuOpen && (
            <div style={{
              position: "absolute", right: 0, top: "calc(100% + 8px)", width: 244, zIndex: 50,
              background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8,
              boxShadow: "var(--shadow-lg)", overflow: "hidden",
            }}>
              <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{user?.name || "Signed in"}</div>
                <div className="mono" style={{ fontSize: 11.5, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                  <Mail size={12} /> {user?.email || "—"}
                </div>
                <span className={`pill ${user?.role === "admin" ? "pill-accent" : "pill-neutral"}`} style={{ marginTop: 8 }}>
                  {user?.role || "customer"}
                </span>
              </div>
              <button
                onClick={() => { setMenuOpen(false); navigate("/settings"); }}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "10px 14px",
                  background: "transparent", border: "none", cursor: "pointer", fontSize: 13,
                  color: "var(--text)", fontFamily: "inherit", borderTop: "1px solid var(--border)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <SettingsIcon size={15} /> Account settings
              </button>
              <button
                onClick={() => { setMenuOpen(false); logout(); }}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "10px 14px",
                  background: "transparent", border: "none", cursor: "pointer", fontSize: 13,
                  color: "var(--err-text)", fontFamily: "inherit",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--err-soft)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <LogOut size={15} /> Sign out
              </button>
            </div>
          )}
        </div>

        {/* Logout */}
        <button
          className="topbar-logout-btn"
          onClick={logout}
          title="Log out"
          onMouseEnter={() => setLogoutHov(true)}
          onMouseLeave={() => setLogoutHov(false)}
          style={{
            width: 34, height: 34, borderRadius: 6,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", border: "1px solid transparent",
            background: logoutHov ? "var(--err-soft)" : "transparent",
            color: logoutHov ? "var(--err-text)" : "var(--text-muted)",
            transition: "background .15s, color .15s",
          }}
        >
          <LogOut size={17} />
        </button>
      </div>
    </header>
  );
}

// ─── App Shell ───────────────────────────────────────────────────────────────

// Persistent while an operator is scoped into a customer's account. This is the control that
// actually prevents accidents — the server-side scope is what makes it safe, but a banner is
// what stops someone forgetting which account they are looking at. Deliberately loud.
function SupportSessionBanner() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.supportSession().then((r) => setS(r.active ? r : null)).catch(() => setS(null));
  useEffect(() => {
    load();
    // Re-check on an interval so the banner disappears when the session expires server-side
    // rather than lingering and implying access the operator no longer has.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);
  if (!s) return null;

  const minsLeft = Math.max(0, Math.round((new Date(s.expires_at) - Date.now()) / 60000));
  const act = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { alert(e.message); } finally { setBusy(false); } };

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "8px 16px", fontSize: 13, fontWeight: 600,
      background: s.write ? "var(--err)" : "var(--warn)", color: "#fff",
    }}>
      <LifeBuoy size={15} style={{ flexShrink: 0 }} />
      <span>
        Support session · <strong>{s.org_name || `Org ${s.org_id}`}</strong> ·{" "}
        {s.write ? "WRITE MODE — changes affect the customer" : "read-only"} · {minsLeft} min left
      </span>
      <span style={{ flex: 1 }} />
      <button
        onClick={() => act(() => api.setSupportWrite(!s.write))}
        disabled={busy}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 6, border: "1px solid rgba(255,255,255,.55)", background: "transparent", color: "#fff", font: "inherit", cursor: "pointer" }}
      >
        <Pencil size={12} /> {s.write ? "Back to read-only" : "Enable changes"}
      </button>
      <button
        onClick={() => act(async () => { await api.closeSupport(); window.location.href = "/clients"; })}
        disabled={busy}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 6, border: "none", background: "rgba(255,255,255,.22)", color: "#fff", font: "inherit", cursor: "pointer" }}
      >
        <X size={12} /> Exit
      </button>
    </div>
  );
}


function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change (nav link tapped on mobile)
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Escape closes the drawer — the backdrop is the only other way out on touch
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  return (
    <div style={{ display: "flex", height: "100%", background: "var(--bg)" }}>
      {/* Topbar padding is generous for desktop; at 320px it was eating the
          breadcrumb's remaining ~65px. Scoped here rather than index.css. */}
      <style>{`
        @media (max-width: 479px) {
          .topbar { padding: 0 10px 0 12px !important; }
        }
      `}</style>
      {/* Backdrop — mobile/tablet only, shown when drawer open */}
      {drawerOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <Sidebar drawerOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", height: "100%" }}>
        <Topbar onMenuClick={() => setDrawerOpen((o) => !o)} />
        <SupportSessionBanner />
        <OnboardingBanner />
        <main key={location.pathname} style={{ flex: 1, minHeight: 0, overflowY: "auto", background: "var(--bg)" }}>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/services/:id" element={<ServiceDetail />} />
            <Route path="/databases" element={<Databases />} />
            <Route path="/databases/:uuid" element={<DatabaseDetail />} />
            <Route path="/new" element={<NewService />} />
            <Route path="/new/app" element={<NewAppService />} />
            <Route path="/new/database" element={<NewDatabase />} />
            <Route path="/new/server" element={user?.role === "admin" ? <Servers /> : <Navigate to="/" replace />} />
            <Route path="/new/storage" element={<ObjectStorage />} />
            <Route path="/github/setup-help" element={<GitHubSetupHelp />} />
            <Route path="/new-database" element={<Navigate to="/new/database" replace />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/deployments" element={<Deployments />} />
            <Route path="/notifications" element={<NotificationSettings />} />
            <Route path="/shared-vars" element={<SharedVars />} />
            <Route path="/servers" element={user?.role === "admin" ? <Servers /> : <Navigate to="/" replace />} />
            <Route path="/fleet" element={<Fleet />} />
            <Route path="/consoles" element={<Consoles />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/team" element={<Team />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/client-services" element={user?.role === "admin" ? <ClientServices /> : <Navigate to="/" replace />} />
            <Route path="/new-git" element={<NewServiceGit />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/object-storage" element={<ObjectStorage />} />
            <Route path="/compose" element={user?.role === "admin" ? <ComposeServices /> : <Navigate to="/" replace />} />
            <Route path="/stripe" element={<StripeAdmin />} />
            <Route path="/email-templates" element={<EmailTemplates />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/usage" element={<Usage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/import" element={<ImportRender />} />
            <Route path="/email" element={<Email />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/admin/projects/:id" element={<ProjectDetail />} />
            <Route path="/docs/custom-domains" element={<DocsCustomDomains />} />
          </Routes>
          </ErrorBoundary>
        </main>
      </div>
      <GateHost />
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

function PwaExperience() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [updateSW, setUpdateSW] = useState(null);
  const [offlineReady, setOfflineReady] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const ios = /iphone|ipad|ipod/i.test(window.navigator.userAgent) || (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
    setIsInstalled(standalone);
    setIsIos(ios);
    let helpDismissed = false;
    try { helpDismissed = window.sessionStorage.getItem("dd-install-help-dismissed") === "1"; } catch { /* storage can be unavailable in private browsing */ }
    if (ios && !standalone && !helpDismissed) {
      setShowInstallHelp(true);
    }

    const onInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      setShowInstallHelp(false);
    };
    window.addEventListener("beforeinstallprompt", onInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    let alive = true;
    let applyUpdate;
    try {
      applyUpdate = registerSW({
        immediate: true,
        onNeedRefresh() { if (alive) setUpdateSW(() => applyUpdate); },
        onOfflineReady() {
          if (!alive) return;
          setOfflineReady(true);
          window.setTimeout(() => { if (alive) setOfflineReady(false); }, 6000);
        },
      });
    } catch (error) {
      // The app remains usable in browsers or development contexts without SW support.
      console.info("Service worker registration unavailable", error);
    }

    return () => {
      alive = false;
      window.removeEventListener("beforeinstallprompt", onInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismissInstall = () => {
    setShowInstallHelp(false);
    try { window.sessionStorage.setItem("dd-install-help-dismissed", "1"); } catch { /* dismissal still applies for this visit */ }
  };
  const install = async () => {
    if (!installPrompt) return setShowInstallHelp(true);
    try {
      installPrompt.prompt();
      await installPrompt.userChoice;
    } finally {
      setInstallPrompt(null);
    }
  };

  const card = {
    position: "fixed", zIndex: 2000, right: 16,
    bottom: "calc(16px + env(safe-area-inset-bottom))", width: "min(360px, calc(100vw - 32px))",
    padding: "14px 16px", borderRadius: 12, border: "1px solid var(--border-strong)",
    background: "var(--surface)", color: "var(--text)", boxShadow: "var(--shadow-lg)",
    fontFamily: "inherit", fontSize: 13,
  };
  const action = {
    padding: "8px 12px", border: 0, borderRadius: 7, background: "var(--accent)",
    color: "#fff", font: "inherit", fontWeight: 600, cursor: "pointer", minHeight: 38,
  };
  const dismiss = {
    border: 0, background: "transparent", color: "var(--text-muted)", cursor: "pointer",
    padding: 6, display: "inline-flex", alignItems: "center", justifyContent: "center",
  };

  return <>
    {!isInstalled && (installPrompt || showInstallHelp) && (
      <aside style={card} aria-label="Install DebutDeploy">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <img src="/app-icon.svg" alt="" width="36" height="36" style={{ flexShrink: 0, borderRadius: 8 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ display: "block", fontSize: 14 }}>Install DebutDeploy</strong>
            <span style={{ display: "block", marginTop: 3, color: "var(--text-muted)", lineHeight: 1.45 }}>
              {isIos && !installPrompt
                ? "In Safari, tap Share, then Add to Home Screen."
                : "Add the control panel to your home screen for a full-screen app experience."}
            </span>
            <div style={{ display: "flex", gap: 8, marginTop: 11 }}>
              {installPrompt && <button style={action} onClick={install}>Install app</button>}
              <button style={dismiss} aria-label="Dismiss install message" onClick={dismissInstall}><X size={16} /></button>
            </div>
          </div>
        </div>
      </aside>
    )}
    {updateSW && (
      <div style={{ ...card, bottom: `calc(${!isInstalled && (installPrompt || showInstallHelp) ? 132 : 16}px + env(safe-area-inset-bottom))` }} role="status" aria-live="polite">
        <strong style={{ display: "block", marginBottom: 5 }}>Update ready</strong>
        <span style={{ color: "var(--text-muted)" }}>A new DebutDeploy version is available.</span>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button style={action} onClick={() => updateSW(true)}>Refresh now</button>
          <button style={dismiss} onClick={() => setUpdateSW(null)}>Later</button>
        </div>
      </div>
    )}
    {offlineReady && !updateSW && (
      <div style={{ ...card, bottom: `calc(${!isInstalled && (installPrompt || showInstallHelp) ? 132 : 16}px + env(safe-area-inset-bottom))`, width: "min(360px, calc(100vw - 32px))" }} role="status" aria-live="polite">
        DebutDeploy is ready to load its app shell offline. Live data and actions still need a connection.
      </div>
    )}
  </>;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PwaExperience />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <ProjectProvider>
                  <OnboardingProvider>
                    <AppShell />
                  </OnboardingProvider>
                </ProjectProvider>
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}
